// client/src/components/BidEstimation/VendorQuotes/QuoteReviewTable.js
import React, { useState, useEffect } from 'react'
import {
  CircularProgress, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableHead, TableRow, Checkbox, Button
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import axios from 'axios'
import CatalogMatchDropdown from './CatalogMatchDropdown'
import CreateCatalogItemDrawer from './CreateCatalogItemDrawer'
import ProductDetailSidebar from './ProductDetailSidebar'
import VariancePanel from './VariancePanel'
import BidEstimationHeader from '../shared/BidEstimationHeader'

const SCOPE_MATCH_AUTO_SELECT_SCORE = 0.85

const confidencePill = {
  high:   { label: 'High Confidence',   className: 'pill-ok' },
  medium: { label: 'Medium Confidence', className: 'pill-warn' },
  low:    { label: 'Low Confidence',    className: 'pill-risk' }
}

const statusPill = {
  parsed:   { label: 'Parsed',   className: 'pill-neutral' },
  reviewed: { label: 'Reviewed', className: 'pill-info' },
  imported: { label: 'Imported', className: 'pill-ok' },
  failed:   { label: 'Failed',   className: 'pill-risk' }
}

const matchStatusPill = {
  auto:      { label: 'Auto',      className: 'pill-warn' },
  confirmed: { label: 'Confirmed', className: 'pill-ok' },
  mapped:    { label: 'Mapped',    className: 'pill-ok' },
  review:    { label: 'Review',    className: 'pill-warn' },
  unmapped:  { label: 'Unmatched', className: 'pill-risk' },
  skipped:   { label: 'Skipped',   className: 'pill-neutral' },
  new_created: { label: 'New',     className: 'pill-info' }
}

const rowBg = {
  mapped:      'var(--ok-tint)',
  new_created: 'var(--info-tint)',
  skipped:     'var(--bg-panel-alt)',
  review:      'var(--warn-tint)'
}

const fmt = (v) => '$' + (v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })

// Seed list for the line-item Category Autocomplete (materialCatalog enum).
// Free-typeable — users can still enter a category not in this list.
const CATEGORY_SUGGESTIONS = [
  'Irrigation', 'Planting', 'Aggregate', 'Lighting',
  'Hardscape', 'Herbicide', 'Fertilizer', 'General'
]

// Scan a line's raw text for part-number / SKU / UPC tokens. Mirrors the
// server parser (vendor_generic.py detect_part_tokens) so the review UI can
// offer click-to-assign suggestion chips. Returns [{ field, label, value }].
const TOKEN_RULES = [
  { field: 'upc', label: 'UPC', re: /UPC\s*#?\s*:?\s*(\d[\d\s-]{9,15}\d)/ig, clean: v => v.replace(/[\s-]/g, '') },
  { field: 'manufacturerSku', label: 'MFR #', re: /(?:MFR|MFG|MPN)\s*(?:P\/?N|#|PART)?\s*:?\s*([A-Z0-9][A-Z0-9._/-]{2,})/ig },
  { field: 'vendorSku', label: 'P/N', re: /\bP\/?N\s*:?\s*([A-Z0-9][A-Z0-9._/-]{2,})/ig },
  { field: 'manufacturerSku', label: 'S/N', re: /\bS\/?N\s*:?\s*([A-Z0-9][A-Z0-9._/-]{2,})/ig },
  { field: 'vendorSku', label: 'SKU', re: /\b(?:SKU|ITEM\s*#|CAT(?:ALOG)?\s*#)\s*:?\s*([A-Z0-9][A-Z0-9._/-]{2,})/ig }
]

function detectLineTokens (rawText) {
  if (!rawText) return []
  const out = []
  const seen = new Set()
  for (const rule of TOKEN_RULES) {
    rule.re.lastIndex = 0
    let m
    while ((m = rule.re.exec(rawText)) !== null) {
      let value = (m[1] || '').trim()
      if (rule.clean) value = rule.clean(value)
      if (rule.field === 'upc' && !(value.length >= 11 && value.length <= 13)) continue
      const key = `${rule.field}|${value}`
      if (seen.has(key)) continue
      seen.add(key)
      out.push({ field: rule.field, label: rule.label, value })
    }
  }
  return out
}

// Standard purchasing units of measure for the line-item UoM Autocomplete.
// Free-typeable — users can still enter a unit not in this list.
const UOM_SUGGESTIONS = [
  'EA', 'EACH', 'LF', 'SF', 'SY', 'CF', 'CY', 'TON', 'LB',
  'GAL', 'QT', 'OZ', 'BAG', 'BOX', 'CASE', 'PALLET', 'ROLL',
  'BUNDLE', 'SHEET', 'LOT', 'HR', 'DAY'
]

// A line's effective extension. Trust the parser's extension when present
// (some vendors — e.g. Ewing — quote a rate as unitPrice, so qty*unitPrice
// is NOT the line total). Fall back to qty*unitPrice only when the parser
// gave no extension. `commitLineItemNumeric` overwrites extension on edit.
const lineExtension = (li) => {
  const ext = Number(li && li.extension)
  if (Number.isFinite(ext) && ext > 0) return ext
  return (Number(li && li.quantity) || 0) * (Number(li && li.unitPrice) || 0)
}

const editInputStyle = {
  width: '100%', minWidth: 56, boxSizing: 'border-box', height: 26, padding: '0 6px',
  border: '1px solid var(--brand, #2e7d32)', borderRadius: 4, background: 'var(--bg-panel)',
  fontSize: 12.5, color: 'var(--ink)', fontFamily: 'inherit', outline: 'none'
}

let editableCellSeq = 0

/**
 * Click-to-edit cell. Shows plain text until clicked, then becomes an input.
 * Commits on blur or Enter; Escape cancels. `type` is 'text' or 'number'.
 * If `suggestions` is provided, the input is a free-typeable Autocomplete
 * (native <datalist>) seeded with those options.
 */
function EditableCell ({ value, display, editable, type = 'text', align = 'left', placeholder, suggestions, onCommit }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = React.useRef(null)
  // Stable datalist id per cell instance
  const listId = React.useRef(suggestions ? `ec-list-${++editableCellSeq}` : null).current

  React.useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const start = () => { if (editable) { setDraft(value == null ? '' : String(value)); setEditing(true) } }
  const finish = (commit) => {
    if (commit) onCommit(draft)
    setEditing(false)
  }

  if (editing) {
    return (
      <>
        <input
          ref={inputRef}
          type={type}
          step={type === 'number' ? 'any' : undefined}
          list={listId || undefined}
          value={draft}
          placeholder={placeholder}
          onChange={e => setDraft(e.target.value)}
          onBlur={() => finish(true)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); finish(true) }
            else if (e.key === 'Escape') { e.preventDefault(); finish(false) }
          }}
          style={{ ...editInputStyle, textAlign: align }}
        />
        {listId && (
          <datalist id={listId}>
            {(suggestions || []).map(opt => <option key={opt} value={opt} />)}
          </datalist>
        )}
      </>
    )
  }

  const shown = display != null ? display : (value === '' || value == null ? '' : value)
  return (
    <div
      onClick={start}
      title={editable ? 'Click to edit' : undefined}
      style={{
        minHeight: 22, padding: '2px 4px', borderRadius: 4,
        textAlign: align, cursor: editable ? 'text' : 'default',
        color: shown === '' ? 'var(--ink-4)' : 'inherit',
        whiteSpace: type === 'text' && align === 'left' ? 'normal' : 'nowrap'
      }}
    >
      {shown === '' ? (editable ? (placeholder || '—') : '—') : shown}
    </div>
  )
}

let headerFieldSeq = 0

/** Labelled header input — Vendor / doc # / PO # / date in the meta bar.
 *  If `suggestions` is given, the input becomes a free-typeable Autocomplete. */
function HeaderField ({ label, value, onChange, disabled, width = 140, type = 'text', suggestions }) {
  const listId = React.useRef(suggestions ? `hf-list-${++headerFieldSeq}` : null).current
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', display: 'block', marginBottom: 4 }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        list={listId || undefined}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        style={{
          height: 32, padding: '0 10px', borderRadius: 6,
          border: '1px solid var(--line-2)', background: 'var(--bg-panel)',
          fontSize: 13, fontFamily: 'var(--f-sans, var(--font-ui))',
          color: 'var(--ink)', outline: 'none', width, boxSizing: 'border-box'
        }}
      />
      {listId && (
        <datalist id={listId}>
          {(suggestions || []).map(opt => <option key={opt} value={opt} />)}
        </datalist>
      )}
    </div>
  )
}

const SaveIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
  </svg>
)

const LinkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
  </svg>
)

const DownloadIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

const SOURCE_LABELS = {
  sku_cache: 'SKU Cache',
  aspire: 'Aspire',
  vendor_catalog: 'Vendor Catalog',
  unmatched: 'Unmatched'
}

const SOURCE_COLORS = {
  sku_cache: { bg: '#e8f5e9', color: '#2e7d32' },
  aspire: { bg: '#e3f2fd', color: '#1565c0' },
  vendor_catalog: { bg: '#f3e5f5', color: '#7b1fa2' },
  unmatched: { bg: '#ffebee', color: '#c62828' }
}

const MatchSummaryBanner = ({ lineItems, autoMatchStatus, vendorName, filter, onFilterChange }) => {
  if (!lineItems || lineItems.length === 0) return null

  if (autoMatchStatus === 'pending') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
        background: 'var(--info-tint, #e3f2fd)', borderRadius: 8, marginBottom: 12, fontSize: 13
      }}>
        <CircularProgress size={14} />
        Auto-matching in progress...
      </div>
    )
  }

  const counts = {}
  let reviewCount = 0
  let mappedCount = 0
  for (const item of lineItems) {
    if (item.mappingStatus === 'review') {
      reviewCount++
    } else if (item.mappingStatus === 'mapped' || item.mappingStatus === 'new_created') {
      mappedCount++
      const src = item.matchSource || 'unmatched'
      counts[src] = (counts[src] || 0) + 1
    }
  }
  const unmappedCount = lineItems.filter(i => i.mappingStatus === 'unmapped').length

  const hasData = Object.keys(counts).length > 0 || reviewCount > 0 || unmappedCount > 0
  if (!hasData) return null

  const chipStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
    padding: '3px 8px', borderRadius: 12, transition: 'background 0.12s',
    background: isActive ? 'var(--ink)' : 'transparent',
    color: isActive ? '#fff' : 'inherit',
    fontWeight: isActive ? 600 : 400
  })

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
      background: 'var(--bg-panel-alt, #f5f5f5)', borderRadius: 8, marginBottom: 12,
      fontSize: 12, flexWrap: 'wrap'
    }}>
      <span style={chipStyle(filter === 'all')} onClick={() => onFilterChange('all')}>
        All ({lineItems.length})
      </span>
      {mappedCount > 0 && (
        <span style={chipStyle(filter === 'mapped')} onClick={() => onFilterChange('mapped')}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: filter === 'mapped' ? '#fff' : '#2e7d32' }} />
          {mappedCount} Matched
        </span>
      )}
      {reviewCount > 0 && (
        <span style={chipStyle(filter === 'review')} onClick={() => onFilterChange('review')}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: filter === 'review' ? '#fff' : '#e65100' }} />
          {reviewCount} Review
        </span>
      )}
      {unmappedCount > 0 && (
        <span style={chipStyle(filter === 'unmapped')} onClick={() => onFilterChange('unmapped')}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: filter === 'unmapped' ? '#fff' : '#c62828' }} />
          {unmappedCount} Unmatched
        </span>
      )}
      <span style={{ marginLeft: 'auto', color: 'var(--ink-4)', fontSize: 11 }}>
        {Object.entries(counts).map(([src, count]) => {
          const label = src === 'vendor_catalog' && vendorName ? vendorName : SOURCE_LABELS[src] || src
          return `${count} ${label}`
        }).join(' · ')}
      </span>
    </div>
  )
}

export default function QuoteReviewTable ({ quoteId, projectId, onBack }) {
  const [quote, setQuote] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [importing, setImporting] = useState(false)
  const [snack, setSnack] = useState(null)
  const [scopeMatches, setScopeMatches] = useState(null)
  const [scopeMatchLoading, setScopeMatchLoading] = useState(false)
  const [scopeMatchSelected, setScopeMatchSelected] = useState(new Set())
  const [skuSuggestions, setSkuSuggestions] = useState({})
  const [statusFilter, setStatusFilter] = useState('all')
  const [sidebarLineItemId, setSidebarLineItemId] = useState(null)
  const [autoMatching, setAutoMatching] = useState(false)
  const [showSource, setShowSource] = useState(true)
  const [bulkSelected, setBulkSelected] = useState(new Set())
  const [bulkActing, setBulkActing] = useState(false)
  const [showScratch, setShowScratch] = useState(false)
  const [scratch, setScratch] = useState('')
  // Line item that triggered "Create New Catalog Item" — opens the drawer
  const [createForLineIdx, setCreateForLineIdx] = useState(null)
  // Vendor names for the header Vendor Autocomplete
  const [vendorOptions, setVendorOptions] = useState([])
  const [linkCandidates, setLinkCandidates] = useState([])
  const [linkedQuoteData, setLinkedQuoteData] = useState(null)

  const fetchQuote = async () => {
    setLoading(true)
    try {
      const { data } = await axios.get(`/api/bid-estimation/vendor-quotes/${quoteId}`)
      setQuote(data)
    } catch (e) {
      console.error('Failed to fetch quote:', e)
    }
    setLoading(false)
  }

  // Silent refresh — updates data without showing loading spinner
  const refreshQuote = async () => {
    try {
      const { data } = await axios.get(`/api/bid-estimation/vendor-quotes/${quoteId}`)
      setQuote(data)
    } catch (e) {
      console.error('Failed to refresh quote:', e)
    }
  }

  const fetchSkuSuggestions = async () => {
    try {
      const { data } = await axios.get(`/api/bid-estimation/vendor-quotes/${quoteId}/sku-suggestions`)
      setSkuSuggestions(data || {})
    } catch (e) {
      console.warn('SKU suggestions fetch failed:', e)
    }
  }

  useEffect(() => { fetchQuote(); fetchSkuSuggestions() }, [quoteId])

  // Vendor names for the header Vendor Autocomplete
  useEffect(() => {
    axios.get('/api/bid-estimation/vendors')
      .then(({ data }) => setVendorOptions(
        Array.isArray(data) ? data.map(v => v.name).filter(Boolean) : []
      ))
      .catch(() => setVendorOptions([]))
  }, [])

  // Poll while auto-match is pending, auto-refresh when it completes
  const prevAutoMatchStatus = React.useRef(null)
  useEffect(() => {
    if (quote?.autoMatchStatus === 'pending') {
      prevAutoMatchStatus.current = 'pending'
      const timer = setInterval(() => refreshQuote(), 3000)
      return () => clearInterval(timer)
    }
    if (prevAutoMatchStatus.current === 'pending' && quote?.autoMatchStatus === 'complete') {
      prevAutoMatchStatus.current = quote.autoMatchStatus
      refreshQuote()
    }
  }, [quote?.autoMatchStatus])

  useEffect(() => {
    if (!quote || quote.documentType !== 'invoice') return
    axios.get(`/api/bid-estimation/vendor-quotes/project/${projectId}`, { params: { documentType: 'quote' } })
      .then(({ data }) => {
        const sameVendor = quote.vendor
          ? data.filter(d => (d.vendor || '').toLowerCase() === quote.vendor.toLowerCase())
          : data
        setLinkCandidates(sameVendor)
        if (!quote.linkedQuoteId && sameVendor.length === 1) {
          // Guard against current state inside the updater — the user may have
          // picked a quote while this candidate fetch was still in flight.
          setQuote(q => q.linkedQuoteId ? q : { ...q, linkedQuoteId: sameVendor[0]._id })
        }
      })
      .catch(() => {})
  }, [quote?._id])

  useEffect(() => {
    if (!quote || quote.documentType !== 'invoice' || !quote.linkedQuoteId) {
      setLinkedQuoteData(null)
      return
    }
    axios.get(`/api/bid-estimation/vendor-quotes/${quote.linkedQuoteId}`)
      .then(({ data }) => setLinkedQuoteData(data))
      .catch(() => setLinkedQuoteData(null))
  }, [quote?.linkedQuoteId])

  const updateLineItem = (index, field, value) => {
    const updated = { ...quote }
    updated.lineItems = [...updated.lineItems]
    updated.lineItems[index] = { ...updated.lineItems[index], [field]: value }
    setQuote(updated)
  }

  const commitLineItemNumeric = (index, field, raw) => {
    const value = raw === '' ? 0 : Number(raw)
    const safe = Number.isNaN(value) ? 0 : value
    const updated = { ...quote, lineItems: [...quote.lineItems] }
    const li = { ...updated.lineItems[index], [field]: safe }
    li.extension = (Number(li.quantity) || 0) * (Number(li.unitPrice) || 0)
    updated.lineItems[index] = li
    setQuote(updated)
  }

  const addLineItem = () => {
    const nextLine = (quote.lineItems || []).reduce((m, li) => Math.max(m, li.lineNumber || 0), 0) + 1
    setQuote({
      ...quote,
      lineItems: [...(quote.lineItems || []), {
        lineNumber: nextLine, category: '', vendorSku: '', description: '',
        quantity: 0, unitPrice: 0, uom: '', extension: 0, mappingStatus: 'unmapped'
      }]
    })
  }

  const deleteLineItem = (index) => {
    setQuote({ ...quote, lineItems: quote.lineItems.filter((_, i) => i !== index) })
  }

  const handleCatalogMatch = (index, catalogItemId, mappingStatus, matchedName) => {
    const updated = { ...quote }
    updated.lineItems = [...updated.lineItems]
    updated.lineItems[index] = {
      ...updated.lineItems[index],
      catalogItemId,
      mappingStatus,
      // Display name for the catalog-match cell so it shows the chosen item
      matchedName: catalogItemId ? (matchedName || updated.lineItems[index].matchedName) : null
    }

    if (mappingStatus === 'mapped' && catalogItemId) {
      const matchedItem = updated.lineItems[index]
      if (matchedItem.vendorSku) {
        let skuCount = 0
        for (let i = 0; i < updated.lineItems.length; i++) {
          const li = updated.lineItems[i]
          if (i === index) continue
          if (li.vendorSku !== matchedItem.vendorSku) continue
          if (li.mappingStatus === 'mapped' || li.mappingStatus === 'skipped') continue
          updated.lineItems[i] = { ...li, catalogItemId, mappingStatus: 'mapped', matchedName: matchedItem.matchedName }
          skuCount++
        }
        if (skuCount > 0) {
          setQuote(updated)
          setSnack({ severity: 'info', message: `Also matched ${skuCount} other line${skuCount > 1 ? 's' : ''} with SKU ${matchedItem.vendorSku}` })
          return
        }
      }
    }
    setQuote(updated)
  }

  const handleSkip = (index) => updateLineItem(index, 'mappingStatus', 'skipped')

  const handleSave = async () => {
    setSaving(true)
    try {
      const { data } = await axios.put(`/api/bid-estimation/vendor-quotes/${quoteId}/line-items`, {
        lineItems: quote.lineItems, vendor: quote.vendor, linkedQuoteId: quote.linkedQuoteId,
        quoteNumber: quote.quoteNumber, poNumber: quote.poNumber, quoteDate: quote.quoteDate,
        total: (quote.lineItems || []).reduce((s, li) => s + lineExtension(li), 0)
      })
      setQuote(data)
      setSnack({ severity: 'success', message: 'Changes saved' })
    } catch (e) {
      setSnack({ severity: 'error', message: 'Save failed: ' + (e.response?.data?.error || e.message) })
    }
    setSaving(false)
  }

  const handleImport = async () => {
    const mappedCount = quote.lineItems.filter(
      li => li.mappingStatus === 'mapped' || li.mappingStatus === 'new_created'
    ).length
    if (mappedCount === 0) {
      setSnack({ severity: 'warning', message: 'No items mapped. Map items to catalog before importing.' })
      return
    }
    setImporting(true)
    try {
      await axios.put(`/api/bid-estimation/vendor-quotes/${quoteId}/line-items`, {
        lineItems: quote.lineItems, vendor: quote.vendor, linkedQuoteId: quote.linkedQuoteId,
        quoteNumber: quote.quoteNumber, poNumber: quote.poNumber, quoteDate: quote.quoteDate,
        total: (quote.lineItems || []).reduce((s, li) => s + lineExtension(li), 0)
      })
      const { data } = await axios.post(`/api/bid-estimation/vendor-quotes/${quoteId}/import`)
      setSnack({
        severity: 'success',
        message: `Imported ${data.imported} items, updated ${data.catalogUpdated} catalog entries, skipped ${data.skipped}`
      })
      fetchQuote()
    } catch (e) {
      const msg = e.response?.status === 409
        ? 'This quote has already been imported'
        : 'Import failed: ' + (e.response?.data?.error || e.message)
      setSnack({ severity: 'error', message: msg })
    }
    setImporting(false)
  }

  const handleAutoMatch = async () => {
    setAutoMatching(true)
    const itemCount = quote.lineItems?.length || 0
    setSnack({ severity: 'info', message: `Auto-matching ${itemCount} line items\u2026 this may take a moment`, persist: true })
    try {
      const res = await axios.post(`/api/bid-estimation/vendor-quotes/${quoteId}/auto-match`)
      const newItems = res.data.newCatalogItems ? `, ${res.data.newCatalogItems} new catalog items created` : ''
      setSnack({ severity: 'success', message: `Auto-matched ${res.data.matchedCount} items, ${res.data.reviewCount} need review${newItems}` })
      await refreshQuote()
    } catch (err) {
      setSnack({ severity: 'error', message: err.response?.data?.error || 'Auto-match failed' })
    }
    setAutoMatching(false)
  }

  const handleMatchToScope = async () => {
    setScopeMatchLoading(true)
    try {
      const { data } = await axios.post(`/api/bid-estimation/vendor-quotes/${quoteId}/match-to-scope`)
      setScopeMatches(data)
      setScopeMatchSelected(new Set(
        data.matches.map((match, i) => match.score >= SCOPE_MATCH_AUTO_SELECT_SCORE ? i : null).filter(i => i != null)
      ))
    } catch (err) {
      setSnack({ severity: 'error', message: err.response?.data?.error || 'Scope matching failed' })
    }
    setScopeMatchLoading(false)
  }

  const handleApplyScopePricing = async () => {
    if (!scopeMatches || scopeMatchSelected.size === 0) return
    const selectedMatches = scopeMatches.matches
      .filter((_, i) => scopeMatchSelected.has(i))
      .map(m => ({ vendorLineIndex: m.vendorLineIndex, scopeItemId: m.scopeItemId, score: m.score }))
    try {
      const { data } = await axios.post(`/api/bid-estimation/vendor-quotes/${quoteId}/apply-scope-pricing`, { matches: selectedMatches })
      setSnack({
        severity: 'success',
        message: `Applied pricing to ${data.applied} scope items${data.catalogUpdated > 0 ? `, updated ${data.catalogUpdated} catalog entries` : ''}`
      })
      setScopeMatches(null)
      setScopeMatchSelected(new Set())
    } catch (err) {
      setSnack({ severity: 'error', message: err.response?.data?.error || 'Apply pricing failed' })
    }
  }

  const handleConfirmMatch = async (idx, aspireItemId) => {
    const item = quote.lineItems[idx]
    try {
      await axios.post(`/api/bid-estimation/vendor-quotes/${quoteId}/match`, {
        lineItemId: item._id, aspireItemId, action: 'confirm'
      })
      const updated = [...quote.lineItems]
      updated[idx] = { ...updated[idx], matchConfidence: 'confirmed' }
      setQuote({ ...quote, lineItems: updated })
      setSnack({ severity: 'success', message: 'Match confirmed \u2014 SKU mapping saved' })
    } catch (err) {
      setSnack({ severity: 'error', message: err.response?.data?.error || 'Confirm failed' })
    }
  }

  const handleRejectMatch = async (idx) => {
    const item = quote.lineItems[idx]
    try {
      await axios.post(`/api/bid-estimation/vendor-quotes/${quoteId}/match`, {
        lineItemId: item._id, aspireItemId: item.catalogItemId, action: 'reject'
      })
      const updated = [...quote.lineItems]
      updated[idx] = { ...updated[idx], catalogItemId: null, mappingStatus: 'unmapped', matchConfidence: null }
      setQuote({ ...quote, lineItems: updated })
    } catch (err) {
      setSnack({ severity: 'error', message: err.response?.data?.error || 'Reject failed' })
    }
  }

  // Indices of auto-matched items eligible for bulk actions
  const autoMatchedIndices = (quote?.lineItems || []).reduce((acc, item, idx) => {
    if (item.matchConfidence === 'auto' && item.mappingStatus === 'mapped' && quote.status !== 'imported') acc.push(idx)
    return acc
  }, [])

  const handleBulkConfirm = async () => {
    if (bulkSelected.size === 0) return
    setBulkActing(true)
    const items = quote.lineItems
    const promises = [...bulkSelected].map(idx => {
      const item = items[idx]
      return axios.post(`/api/bid-estimation/vendor-quotes/${quoteId}/match`, {
        lineItemId: item._id, aspireItemId: item.catalogItemId, action: 'confirm'
      })
    })
    try {
      await Promise.all(promises)
      const updated = [...items]
      for (const idx of bulkSelected) {
        updated[idx] = { ...updated[idx], matchConfidence: 'confirmed' }
      }
      setQuote({ ...quote, lineItems: updated })
      setSnack({ severity: 'success', message: `Confirmed ${bulkSelected.size} matches` })
      setBulkSelected(new Set())
    } catch (err) {
      setSnack({ severity: 'error', message: err.response?.data?.error || 'Bulk confirm failed' })
    }
    setBulkActing(false)
  }

  const handleBulkReject = async () => {
    if (bulkSelected.size === 0) return
    setBulkActing(true)
    const items = quote.lineItems
    const promises = [...bulkSelected].map(idx => {
      const item = items[idx]
      return axios.post(`/api/bid-estimation/vendor-quotes/${quoteId}/match`, {
        lineItemId: item._id, aspireItemId: item.catalogItemId, action: 'reject'
      })
    })
    try {
      await Promise.all(promises)
      const updated = [...items]
      for (const idx of bulkSelected) {
        updated[idx] = { ...updated[idx], catalogItemId: null, mappingStatus: 'unmapped', matchConfidence: null }
      }
      setQuote({ ...quote, lineItems: updated })
      setSnack({ severity: 'success', message: `Rejected ${bulkSelected.size} matches` })
      setBulkSelected(new Set())
    } catch (err) {
      setSnack({ severity: 'error', message: err.response?.data?.error || 'Bulk reject failed' })
    }
    setBulkActing(false)
  }

  if (loading) {
    return <div className="hmi-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}><CircularProgress /></div>
  }
  if (!quote) {
    return <div className="hmi-page"><div className="hmi-body"><div style={{ color: 'var(--risk)', padding: 40 }}>Quote not found</div></div></div>
  }

  const badge = confidencePill[quote.parseConfidence] || confidencePill.low
  const sp = statusPill[quote.status] || statusPill.parsed
  const isInvoice = quote.documentType === 'invoice'
  const docWord = isInvoice ? 'Invoice' : 'Quote'
  // Total is derived from the line items (qty x price), so it stays correct
  // when the parser missed it or after the user edits line items.
  const computedTotal = (quote.lineItems || []).reduce(
    (sum, li) => sum + lineExtension(li), 0
  )
  const extractedText = String(quote.parserRawText || '').trim()

  return (
    <div className="hmi-page" style={{ position: 'fixed', inset: 0, zIndex: 1200, overflow: 'auto' }}>
      <BidEstimationHeader
        breadcrumbs={[
          { label: 'Bid Estimation', to: '/bid-estimation' },
          ...(projectId ? [{ label: 'Project', to: `/bid-estimation/${projectId}` }] : []),
          { label: 'Vendor Docs', to: onBack },
          { label: `${quote.vendor || docWord} ${quote.quoteNumber || ''}`.trim() }
        ]}
        title={`${quote.vendor || 'Unknown Vendor'} \u2014 ${docWord} ${quote.quoteNumber || ''}`}
        statusChip={{ label: badge.label, className: badge.className }}
        actions={(
          <>
            <button className="hmi-btn hmi-btn-outline" onClick={handleSave} disabled={saving || quote.status === 'imported'}>
              <SaveIcon /> Save
            </button>
            <button className="hmi-btn hmi-btn-outline" onClick={handleAutoMatch} disabled={autoMatching || saving || quote.status === 'imported'}>
              {autoMatching ? <><CircularProgress size={13} style={{ marginRight: 4 }} /> Matching…</> : 'Auto-Match'}
            </button>
            <button
              className="hmi-btn hmi-btn-outline"
              style={{ borderColor: 'var(--brand)', color: 'var(--brand-deep)' }}
              onClick={handleMatchToScope}
              disabled={scopeMatchLoading}
            >
              {scopeMatchLoading ? <CircularProgress size={14} /> : <LinkIcon />} Match to Scope
            </button>
            <button
              className="hmi-btn"
              style={quote.status === 'imported'
                ? { background: 'var(--bg-panel-alt)', color: 'var(--ink-3)', borderColor: 'var(--line-2)', cursor: 'default' }
                : { background: 'var(--brand)', color: '#fff', borderColor: 'var(--brand-deep)' }
              }
              onClick={handleImport}
              disabled={importing || quote.status === 'imported'}
            >
              <DownloadIcon /> {quote.status === 'imported' ? 'Imported' : 'Import to Catalog'}
            </button>
          </>
        )}
      />

      <div className="hmi-body">
        {/* Quote meta bar */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
          <HeaderField label="Vendor" width={170}
            value={quote.vendor || ''}
            disabled={quote.status === 'imported'}
            suggestions={vendorOptions}
            onChange={v => setQuote({ ...quote, vendor: v })} />
          <HeaderField label={`${docWord} #`} width={130}
            value={quote.quoteNumber || ''}
            disabled={quote.status === 'imported'}
            onChange={v => setQuote({ ...quote, quoteNumber: v })} />
          <HeaderField label="PO #" width={130}
            value={quote.poNumber || ''}
            disabled={quote.status === 'imported'}
            onChange={v => setQuote({ ...quote, poNumber: v })} />
          <HeaderField label={`${docWord} Date`} width={150} type="date"
            value={quote.quoteDate ? new Date(quote.quoteDate).toISOString().slice(0, 10) : ''}
            disabled={quote.status === 'imported'}
            onChange={v => setQuote({ ...quote, quoteDate: v || null })} />
          {isInvoice && (
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', display: 'block', marginBottom: 4 }}>
                Linked Quote
              </label>
              <select
                value={quote.linkedQuoteId || ''}
                onChange={e => setQuote({ ...quote, linkedQuoteId: e.target.value || null })}
                disabled={quote.status === 'imported'}
                style={{ height: 32, padding: '0 10px', borderRadius: 6, border: '1px solid var(--line-2)', background: 'var(--bg-panel)', fontSize: 13, color: 'var(--ink)' }}
              >
                <option value="">— none —</option>
                {linkCandidates.map(c => (
                  <option key={c._id} value={c._id}>
                    {(c.vendor || 'Quote')} {c.quoteNumber || ''} ({c.lineItemCount || 0} items)
                  </option>
                ))}
              </select>
            </div>
          )}
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)', display: 'flex', gap: 16, alignItems: 'center', paddingBottom: 7 }}>
            <span>Total: <b style={{ fontFamily: 'var(--f-mono, var(--font-mono))', color: 'var(--ink)' }}>{fmt(computedTotal)}</b></span>
            <span>{quote.lineItems?.length || 0} line items</span>
            <span className={`pill ${sp.className}`}><span className="dot" />{sp.label}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', paddingBottom: 3 }}>
            <button
              className={`hmi-btn hmi-btn-sm ${showScratch ? 'hmi-btn-primary' : 'hmi-btn-outline'}`}
              onClick={() => setShowScratch(!showScratch)}
            >
              {showScratch ? 'Hide Scratch' : 'Scratch Pad'}
            </button>
            {quote.s3Url && (
              <button
                className={`hmi-btn hmi-btn-sm ${showSource ? 'hmi-btn-primary' : 'hmi-btn-outline'}`}
                onClick={() => setShowSource(!showSource)}
              >
                {showSource ? 'Hide Source' : 'View Source'}
              </button>
            )}
          </div>
        </div>

        {/* Scratch pad - ephemeral staging area for cleaning up pasted PDF text */}
        {showScratch && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>
                Scratch Pad {'\u2014'} paste & clean up PDF text, then copy into a field
              </label>
              <span style={{
                fontSize: 9, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
                padding: '2px 8px', borderRadius: 10, background: 'var(--bg-panel-alt, #eee)', color: 'var(--ink-3)'
              }}>
                Not Saved
              </span>
              <span
                onClick={() => setScratch('')}
                style={{
                  fontSize: 9, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
                  padding: '2px 8px', borderRadius: 10,
                  background: scratch ? 'var(--risk-tint, #ffebee)' : 'var(--bg-panel-alt, #eee)',
                  color: scratch ? 'var(--risk, #c62828)' : 'var(--ink-4)',
                  cursor: scratch ? 'pointer' : 'default'
                }}
                title="Clear scratch pad"
              >
                Clear
              </span>
              {extractedText && (
                <span
                  onClick={() => setScratch(extractedText)}
                  style={{
                    fontSize: 9, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
                    padding: '2px 8px', borderRadius: 10,
                    background: 'var(--brand-tint, #e8f2eb)', color: 'var(--brand-deep, #1d5b38)',
                    cursor: 'pointer'
                  }}
                  title="Load parsed/OCR text into the scratch pad"
                >
                  Load Extracted Text
                </span>
              )}
            </div>
            <textarea
              value={scratch}
              onChange={e => setScratch(e.target.value)}
              placeholder={'Select text in the source PDF, copy it, and paste here to tidy it up before pasting into a line item or header field\u2026'}
              rows={4}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '8px 10px',
                borderRadius: 6, border: '1px solid var(--line-2)', background: 'var(--bg-panel)',
                fontSize: 13, fontFamily: 'var(--f-mono, var(--font-mono))', color: 'var(--ink)',
                outline: 'none', resize: 'vertical'
              }}
            />
          </div>
        )}

        {/* Split layout: Source + Table */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {showSource && quote.s3Url && (
          <div style={{
            width: '45%', minWidth: 340, flexShrink: 0,
            position: 'sticky', top: 80,
            height: 'calc(100vh - 100px)',
            border: '1px solid var(--line-2)', borderRadius: 8,
            background: '#fafafa', overflow: 'hidden'
          }}>
            <iframe
              src={quote.s3Url}
              title="Source Quote PDF"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        )}

        {/* Line items table — fixed-height panel; only the rows scroll */}
        <div className="hmi-panel" style={{
          flex: 1, minWidth: 0,
          position: 'sticky', top: 80,
          height: 'calc(100vh - 100px)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden'
        }}>
          <MatchSummaryBanner
            lineItems={quote.lineItems}
            autoMatchStatus={quote.autoMatchStatus}
            vendorName={quote.vendor}
            filter={statusFilter}
            onFilterChange={setStatusFilter}
          />
          {bulkSelected.size > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
              background: 'var(--brand-tint, #e3f2fd)', borderRadius: 8, marginBottom: 8, fontSize: 12.5
            }}>
              <b>{bulkSelected.size} selected</b>
              <button
                className="hmi-btn hmi-btn-sm"
                style={{ background: 'var(--ok)', color: '#fff', border: 'none' }}
                onClick={handleBulkConfirm}
                disabled={bulkActing}
              >
                {bulkActing ? 'Working...' : 'Confirm All'}
              </button>
              <button
                className="hmi-btn hmi-btn-sm"
                style={{ background: 'var(--risk)', color: '#fff', border: 'none' }}
                onClick={handleBulkReject}
                disabled={bulkActing}
              >
                Reject All
              </button>
              <button
                className="hmi-btn hmi-btn-ghost hmi-btn-sm"
                onClick={() => setBulkSelected(new Set())}
                style={{ fontSize: 11 }}
              >
                Clear
              </button>
            </div>
          )}
          <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <table className="hmi-table" style={{ minWidth: 1240 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-panel, #fff)' }}>
              <tr>
                {autoMatchedIndices.length > 0 && (
                  <th style={{ width: 32, padding: '4px 0', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={autoMatchedIndices.length > 0 && autoMatchedIndices.every(i => bulkSelected.has(i))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setBulkSelected(new Set(autoMatchedIndices))
                        } else {
                          setBulkSelected(new Set())
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                )}
                <th style={{ width: 40 }}>#</th>
                <th style={{ width: 90 }}>Category</th>
                <th style={{ width: 90 }}>SKU</th>
                <th style={{ minWidth: 220 }}>Description</th>
                <th style={{ textAlign: 'right', width: 90 }}>Qty</th>
                <th style={{ textAlign: 'right', width: 110 }}>Price</th>
                <th style={{ width: 80 }}>UOM</th>
                <th style={{ textAlign: 'right', width: 110 }}>Extension</th>
                <th style={{ width: 150 }}>Catalog Match</th>
                <th style={{ textAlign: 'center', width: 100 }}>Match Status</th>
                <th style={{ textAlign: 'center', width: 90 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {(quote.lineItems || []).map((item, idx) => ({ item, idx })).filter(({ item }) => {
                if (statusFilter === 'all') return true
                if (statusFilter === 'mapped') return item.mappingStatus === 'mapped' || item.mappingStatus === 'new_created'
                if (statusFilter === 'review') return item.mappingStatus === 'review'
                if (statusFilter === 'unmapped') return item.mappingStatus === 'unmapped'
                return true
              }).map(({ item, idx }) => {
                const editable = quote.status !== 'imported'
                const bg = rowBg[item.mappingStatus] || undefined
                const ms = item.matchConfidence === 'auto' && item.mappingStatus === 'mapped'
                  ? matchStatusPill.auto
                  : (item.matchConfidence === 'confirmed' || (item.mappingStatus === 'mapped' && !item.matchConfidence))
                    ? matchStatusPill.mapped
                    : matchStatusPill[item.mappingStatus] || null

                return (
                  <tr key={item._id || idx} style={bg ? { background: bg } : undefined}>
                    {autoMatchedIndices.length > 0 && (
                      <td style={{ textAlign: 'center', padding: '4px 0' }}>
                        {item.matchConfidence === 'auto' && item.mappingStatus === 'mapped' && quote.status !== 'imported' ? (
                          <input
                            type="checkbox"
                            checked={bulkSelected.has(idx)}
                            onChange={(e) => {
                              setBulkSelected(prev => {
                                const next = new Set(prev)
                                if (e.target.checked) next.add(idx)
                                else next.delete(idx)
                                return next
                              })
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                        ) : null}
                      </td>
                    )}
                    <td style={{ fontFamily: 'var(--f-mono, var(--font-mono))', fontSize: 11, color: 'var(--ink-3)' }}>{item.lineNumber}</td>
                    <td style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                      <EditableCell
                        value={item.category}
                        editable={editable}
                        placeholder="Category"
                        suggestions={CATEGORY_SUGGESTIONS}
                        onCommit={v => updateLineItem(idx, 'category', v)}
                      />
                    </td>
                    <td style={{ fontFamily: 'var(--f-mono, var(--font-mono))', fontSize: 11 }}>
                      {item.vendorSku ? (
                        <span
                          style={{
                            color: 'var(--brand-deep, var(--brand))', cursor: 'pointer',
                            textDecoration: 'none', borderBottom: '1px dashed var(--brand-deep, var(--brand))'
                          }}
                          onClick={(e) => { e.stopPropagation(); setSidebarLineItemId(item._id) }}
                          title="View product details"
                        >
                          {item.vendorSku}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--ink-3)' }}>{'\u2014'}</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12.5 }}>
                      <EditableCell
                        value={item.description}
                        editable={editable}
                        placeholder="Description"
                        onCommit={v => updateLineItem(idx, 'description', v)}
                      />
                      {editable && (() => {
                        const tokens = detectLineTokens(item.rawText)
                        // Hide a chip if that field already holds its value
                        const open = tokens.filter(t => (item[t.field] || '') !== t.value)
                        if (open.length === 0) return null
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                            {open.map((t, ti) => (
                              <span
                                key={ti}
                                onClick={() => updateLineItem(idx, t.field, t.value)}
                                title={`Assign to ${t.label}`}
                                style={{
                                  fontSize: 10, padding: '1px 7px', borderRadius: 10, cursor: 'pointer',
                                  background: 'var(--info-tint, #e3f2fd)', color: 'var(--info, #1565c0)',
                                  border: '1px solid var(--info, #1565c0)', whiteSpace: 'nowrap'
                                }}
                              >
                                + {t.label}: {t.value}
                              </span>
                            ))}
                          </div>
                        )
                      })()}
                    </td>
                    <td className="num">
                      <EditableCell
                        value={item.quantity ?? 0}
                        editable={editable}
                        type="number"
                        align="right"
                        onCommit={v => commitLineItemNumeric(idx, 'quantity', v)}
                      />
                    </td>
                    <td className="num">
                      <EditableCell
                        value={item.unitPrice ?? 0}
                        display={fmt(item.unitPrice)}
                        editable={editable}
                        type="number"
                        align="right"
                        onCommit={v => commitLineItemNumeric(idx, 'unitPrice', v)}
                      />
                    </td>
                    <td style={{ fontFamily: 'var(--f-mono, var(--font-mono))', fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase' }}>
                      <EditableCell
                        value={item.uom}
                        editable={editable}
                        placeholder="UOM"
                        suggestions={UOM_SUGGESTIONS}
                        onCommit={v => updateLineItem(idx, 'uom', v)}
                      />
                    </td>
                    <td className="num">{fmt(lineExtension(item))}</td>
                    <td>
                      {quote.status !== 'imported' ? (
                        <CatalogMatchDropdown
                          value={item.catalogItemId}
                          matchedName={item.matchedName}
                          lineItem={item}
                          matchCandidates={item.matchCandidates || []}
                          skuSuggestion={skuSuggestions[item.vendorSku] || null}
                          onChange={(catId, status, name) => handleCatalogMatch(idx, catId, status, name)}
                          onRequestCreate={() => setCreateForLineIdx(idx)}
                        />
                      ) : (
                        ms && <span className={`pill ${ms.className}`}><span className="dot" />{ms.label}</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {ms && <span className={`pill ${ms.className}`}><span className="dot" />{ms.label}</span>}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {item.matchConfidence === 'auto' && item.mappingStatus === 'mapped' && quote.status !== 'imported' && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                          <button
                            className="hmi-btn hmi-btn-ghost hmi-btn-sm"
                            style={{ width: 26, height: 24, padding: 0, color: 'var(--ok)', fontSize: 14 }}
                            title="Confirm match"
                            onClick={() => handleConfirmMatch(idx, item.catalogItemId)}
                          >
                            {'\u2713'}
                          </button>
                          <button
                            className="hmi-btn hmi-btn-ghost hmi-btn-sm"
                            style={{ width: 26, height: 24, padding: 0, color: 'var(--risk)', fontSize: 14 }}
                            title="Reject match"
                            onClick={() => handleRejectMatch(idx)}
                          >
                            {'\u2717'}
                          </button>
                        </div>
                      )}
                      {quote.status !== 'imported' && item.mappingStatus !== 'skipped' && item.matchConfidence !== 'auto' && (
                        <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={() => handleSkip(idx)} style={{ fontSize: 11 }}>
                          Skip
                        </button>
                      )}
                      {item.mappingStatus === 'skipped' && (
                        <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={() => updateLineItem(idx, 'mappingStatus', 'unmapped')} style={{ fontSize: 11 }}>
                          Unskip
                        </button>
                      )}
                      {editable && (
                        <button className="hmi-btn hmi-btn-ghost hmi-btn-sm"
                          title="Delete line" onClick={() => deleteLineItem(idx)}
                          style={{ fontSize: 11, color: 'var(--risk)' }}>
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {quote.status !== 'imported' && (
            <button className="hmi-btn hmi-btn-outline hmi-btn-sm" onClick={addLineItem}
              style={{ margin: '8px 0' }}>
              + Add Line Item
            </button>
          )}
          </div>{/* end scroll container */}
        </div>
        </div>{/* end split layout */}

        {isInvoice && quote.linkedQuoteId && linkedQuoteData && (
          <VariancePanel
            invoiceLineItems={quote.lineItems}
            quoteLineItems={linkedQuoteData.lineItems}
          />
        )}

        {quote.parseError && (
          <div style={{
            padding: '10px 14px', borderRadius: 8,
            background: 'var(--warn-tint)', border: '1px solid var(--warn)',
            fontSize: 12.5, color: 'var(--ink)'
          }}>
            Parse warning: {quote.parseError}
          </div>
        )}
      </div>

      {/* Scope Match Review Dialog */}
      {scopeMatches && (
        <Dialog open maxWidth="md" fullWidth onClose={() => setScopeMatches(null)}>
          <DialogTitle>
            Match Vendor Quote to Scope Items
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
              {scopeMatches.vendor} {'\u2014'} {scopeMatches.matchedLines} of {scopeMatches.totalLines} lines matched
            </div>
          </DialogTitle>
          <DialogContent>
            {scopeMatches.matches.length === 0 ? (
              <div style={{ color: 'var(--ink-3)', padding: 20 }}>No matches found. Try adjusting scope item descriptions.</div>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={scopeMatchSelected.size === scopeMatches.matches.length}
                        indeterminate={scopeMatchSelected.size > 0 && scopeMatchSelected.size < scopeMatches.matches.length}
                        onChange={(e) => {
                          setScopeMatchSelected(e.target.checked
                            ? new Set(scopeMatches.matches.map((_, i) => i))
                            : new Set()
                          )
                        }}
                      />
                    </TableCell>
                    <TableCell style={{ fontWeight: 600 }}>Vendor Line</TableCell>
                    <TableCell align="right" style={{ fontWeight: 600 }}>Unit Price</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>Scope Item</TableCell>
                    <TableCell align="center" style={{ fontWeight: 600 }}>Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scopeMatches.matches.map((m, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={scopeMatchSelected.has(idx)}
                          onChange={(e) => {
                            setScopeMatchSelected(prev => {
                              const next = new Set(prev)
                              if (e.target.checked) next.add(idx)
                              else next.delete(idx)
                              return next
                            })
                          }}
                        />
                      </TableCell>
                      <TableCell style={{ fontSize: 12 }}>{m.vendorDesc}</TableCell>
                      <TableCell align="right" style={{ fontSize: 12, fontWeight: 600 }}>{fmt(m.vendorUnitPrice)}</TableCell>
                      <TableCell style={{ fontSize: 12 }}>{m.scopeDesc}</TableCell>
                      <TableCell align="center">
                        <span className={`pill ${m.score >= 0.7 ? 'pill-ok' : m.score >= 0.5 ? 'pill-warn' : 'pill-risk'}`} style={{ fontSize: 10 }}>
                          <span className="dot" />{Math.round(m.score * 100)}%
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScopeMatches(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleApplyScopePricing}
              disabled={scopeMatchSelected.size === 0}
              style={{ backgroundColor: 'var(--brand)', color: '#fff' }}
            >
              Apply Pricing ({scopeMatchSelected.size} items)
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <ProductDetailSidebar
        quoteId={quoteId}
        lineItemId={sidebarLineItemId}
        open={!!sidebarLineItemId}
        onClose={() => setSidebarLineItemId(null)}
      />

      <CreateCatalogItemDrawer
        open={createForLineIdx != null}
        lineItem={createForLineIdx != null ? quote.lineItems[createForLineIdx] : null}
        defaultVendor={quote.vendor || ''}
        onClose={() => setCreateForLineIdx(null)}
        onCreated={(catalogItemId, catalogItemName) => {
          if (createForLineIdx != null) {
            handleCatalogMatch(createForLineIdx, catalogItemId, 'new_created', catalogItemName)
          }
          setCreateForLineIdx(null)
          setSnack({ severity: 'success', message: 'Catalog item created and linked' })
        }}
      />

      <Snackbar open={!!snack} autoHideDuration={snack?.persist ? null : 4000} onClose={() => { if (!snack?.persist) setSnack(null) }}>
        {snack && <Alert severity={snack.severity}>{snack.message}</Alert>}
      </Snackbar>
    </div>
  )
}
